import app from 'flarum/forum/app';
import Component from 'flarum/common/Component';
import Button from 'flarum/common/components/Button';
import m from 'mithril';

/*
 * 【朗读主帖】按钮（1.8.36.15，云端 TTS 诊断/预热 1.8.36.21）

 *
 * 【方案】浏览器内置 Web Speech API（window.speechSynthesis），零后端、零成本。
 *   点击 -> 从 DOM 取首楼正文 -> 按标点切段 -> 逐段 SpeechSynthesisUtterance 朗读。
 *
 * 【为什么只取首楼】
 *   用户明确只要「主帖」；而且 Flarum 的帖子流是懒加载的，
 *   一次把全部回复读完既慢又容易读到半截内容，需求上也不需要。
 *
 * 【四个必须处理的浏览器坑】
 *   1) iOS / 微信：首次 speak 必须由用户手势触发，且要先喂一个空 utterance 解锁，
 *      否则第一次点击无声（第二次才开始响）。下面 unlock() 就是干这个的。
 *   2) Chrome 长文本 15 秒截断 bug：合成到 ~15s 会自己停。
 *      用「分段 + 每 10 秒 pause()/resume() 续命」两条一起保证长文不中断。
 *      只在 Chromium 系上开续命（Safari 上 pause/resume 反而会卡死）。
 *   3) voices 异步：getVoices() 首次可能返回空数组，要监听 onvoiceschanged 再取。
 *   4) 中文语音不一定存在（尤其 Android 微信 X5 内核）：取不到 zh 语音就交给浏览器默认，
 *      读不出来会在 onerror 里提示，不静默失败。
 * ========================================================================== */

// 单段最大字数：约 30~40 秒的中文，配合续命机制足够安全
const MAX_CHUNK = 120;

// 编译标记：线上抓 forum.js grep 这个串就能确认这版是否真的部署上了
const JS_BUILD = 'ACME_SPEAK_1_8_36_21';

/*
 * 朗读提示默认 5 秒后自动关闭（1.8.36.26）。
 * 朗读的错误提示（不支持 / 没内容 / 合成失败）只是告知一声，
 * 一直挂在页面顶部会挡住内容，用户还得手动点叉。
 */
const ALERT_DURATION = 5000;

// 提示 id -> 定时器。重复弹同一条时先清旧的，别把新提示提前关掉。
const ALERT_TIMERS = {};

/**
 * 弹一条会自动关闭的提示。
 *
 * 【为什么不能直接 app.alerts.show()】
 * Flarum 核心的 app.alerts.show() 返回的是提示 id（number），
 * 而且**没有内置自动消失** —— 不手动 app.alerts.dismiss(id) 就会一直挂着。
 * 所以这里包一层：弹完起个定时器，到点自己 dismiss。
 */
function notify(attrs, children, ms) {
  if (typeof app === 'undefined' || !app.alerts) {
    return null;
  }

  const id = app.alerts.show(attrs, children);

  if (!id) {
    return id;
  }

  if (ALERT_TIMERS[id]) {
    clearTimeout(ALERT_TIMERS[id]);
  }

  ALERT_TIMERS[id] = setTimeout(() => {
    try {
      app.alerts.dismiss(id);
    } catch (e) {
      // 提示可能已经被用户手动关掉了，忽略
    }

    delete ALERT_TIMERS[id];
  }, ms === undefined ? ALERT_DURATION : ms);

  return id;
}

/*
 * 切段用的句子边界（中英文标点都算）。
 *
 * 【为什么不用「后行断言」正则来切句】
 * 那是 lookbehind，iOS Safari 16.4 以下完全不支持 —— 微信内置浏览器若跑在旧 WebKit 上，
 * 遇到这个正则字面量会直接 SyntaxError，整个 forum.js 挂掉（不只是朗读功能失效）。
 * 老老实实逐字符扫，兼容性 100%。
 */
const SENTENCE_ENDS = '。！？；.!?;\n';

function isChromium() {
  const ua = navigator.userAgent || '';

  return /Chrome|Chromium|CriOS/i.test(ua) && !/Edg|OPR|Firefox|Safari\/.*Version/i.test(ua);
}

/*
 * 从当前 DOM 里取首楼正文的纯文本。
 * 帖子正文是渲染后的 HTML（含 <p>、<br>、代码块、@提及），
 * 用 innerText 能拿到「人读起来顺」的换行与空格，比自己正则剥标签可靠得多。
 */
function firstPostText() {
  const selectors = ['.PostStream .Post-body', '.Post-body'];

  for (let i = 0; i < selectors.length; i++) {
    const el = document.querySelector(selectors[i]);

    if (el && el.innerText && el.innerText.trim()) {
      return el.innerText.trim();
    }
  }

  return '';
}

function splitSentences(text) {
  const out = [];
  let buf = '';
  const str = String(text);

  for (let i = 0; i < str.length; i++) {
    buf += str[i];

    if (SENTENCE_ENDS.indexOf(str[i]) >= 0) {
      out.push(buf);
      buf = '';
    }
  }

  if (buf) {
    out.push(buf);
  }

  return out;
}

function splitText(text) {
  const pieces = splitSentences(text).filter((s) => s && s.trim());
  const chunks = [];
  let buf = '';

  pieces.forEach((piece) => {
    if ((buf + piece).length > MAX_CHUNK && buf) {
      chunks.push(buf);
      buf = piece;
    } else {
      buf += piece;
    }

    // 极长的一句话（没有标点）硬切，避免单段过长触发截断
    while (buf.length > MAX_CHUNK * 2) {
      chunks.push(buf.slice(0, MAX_CHUNK * 2));
      buf = buf.slice(MAX_CHUNK * 2);
    }
  });

  if (buf.trim()) {
    chunks.push(buf);
  }

  return chunks.map((c) => c.trim()).filter(Boolean);
}

// 已经预热过的帖子（避免同一帖反复触发服务端合成检查，1.8.36.21）
const PREWARMED = {};

export default class SpeakButton extends Component {
  oninit(vnode) {
    super.oninit(vnode);

    this.discussion = this.attrs.discussion;
    this.speaking = false;
    this.chunks = [];
    this.index = 0;
    this.stopped = true;
    this.utterance = null;
    this.keepAliveTimer = null;

    // 云 TTS 播放用的 <audio>（1.8.36.20）
    this.cloudAudio = null;
    // 首次合成要调腾讯云，会有几秒等待，按钮显示「生成中…」
    this.preparing = false;
    // 失败提示只弹一次（play() 的 reject 与 audio.onerror 会同时来）
    this.reported = false;

    // 把编译标记挂到 window，方便手机上诊断（?acmeDiag=1 也能看到）
    try {
      window.__ACME_SPEAK_BUILD__ = JS_BUILD;
    } catch (e) {
      // 忽略
    }

    /*
     * 支持判定。注意这里**只用于点击时的提示**，不再决定按钮渲不渲染（1.8.36.19）。
     * 两个 API 缺一个就认定不支持：只有 speak 没有构造器（或反之）都发不出声。
     */
    this.supported =
      typeof window !== 'undefined' && !!window.speechSynthesis && typeof window.SpeechSynthesisUtterance === 'function';
    this.voice = null;

    if (this.supported) {
      this.loadVoice();

      // 坑 3：voices 可能是异步就绪的
      if (typeof window.speechSynthesis.onvoiceschanged !== 'undefined') {
        window.speechSynthesis.onvoiceschanged = () => this.loadVoice();
      }
    }
  }

  loadVoice() {
    const voices = window.speechSynthesis.getVoices() || [];

    this.voice =
      voices.find((v) => /^zh|^cmn/i.test(v.lang || '')) || voices.find((v) => /chinese|中文|普通话/i.test(v.name || '')) || null;

    m.redraw();
  }

  // 坑 1：iOS/微信首次解锁 —— 必须先喂一个空 utterance
  unlock() {
    try {
      const u = new window.SpeechSynthesisUtterance('');
      u.volume = 0;
      window.speechSynthesis.speak(u);
    } catch (e) {
      // 解锁失败无所谓，不影响后续正常朗读
    }
  }

  startKeepAlive() {
    this.stopKeepAlive();

    if (!isChromium()) {
      return;
    }

    // 坑 2：Chrome ~15s 会因 GC/计时器把 utterance 掐断，pause+resume 可续命
    this.keepAliveTimer = setInterval(() => {
      const synth = window.speechSynthesis;

      if (synth && synth.speaking && !synth.paused) {
        synth.pause();
        synth.resume();
      }
    }, 10000);
  }

  stopKeepAlive() {
    if (this.keepAliveTimer) {
      clearInterval(this.keepAliveTimer);
      this.keepAliveTimer = null;
    }
  }

  playNext() {
    if (this.stopped || this.index >= this.chunks.length) {
      this.finish();

      return;
    }

    const synth = window.speechSynthesis;
    const u = new window.SpeechSynthesisUtterance(this.chunks[this.index]);

    u.lang = 'zh-CN';
    if (this.voice) {
      u.voice = this.voice;
    }
    u.rate = 1;
    u.pitch = 1;
    u.volume = 1;

    u.onend = () => {
      if (this.stopped) {
        return;
      }

      this.index += 1;
      this.playNext();
    };

    u.onerror = () => {
      this.finish();

      notify({ type: 'warning' }, app.translator.trans('acme-wechat-sms.forum.speak_failed'));
    };

    this.utterance = u;

    this.index += 1;
    synth.speak(u);
    this.startKeepAlive();
  }

  finish() {
    this.stopped = true;
    this.speaking = false;
    this.preparing = false;
    this.reported = false;
    this.utterance = null;
    this.stopKeepAlive();
    m.redraw();
  }

  stop() {
    try {
      window.speechSynthesis.cancel();
    } catch (e) {
      // 忽略
    }

    // 云 TTS：把 <audio> 停掉并释放
    if (this.cloudAudio) {
      try {
        this.cloudAudio.pause();
        this.cloudAudio.currentTime = 0;
        // 置空 src 才能真正中断下载（首次合成时长文会持续拉流）
        this.cloudAudio.src = '';

        // 从 DOM 里摘掉（1.8.36.21 为了微信兼容插进去的）
        if (this.cloudAudio.parentNode) {
          this.cloudAudio.parentNode.removeChild(this.cloudAudio);
        }
      } catch (e) {
        // 忽略
      }

      this.cloudAudio = null;
    }

    this.finish();
  }

  toggle() {
    if (this.speaking) {
      this.stop();

      return;
    }

    /*
     * 浏览器没有内置语音（手机微信就是这个情况）→ 走云端合成。
     * 两条路都没有时才提示不支持（1.8.36.19 / .20）。
     */
    if (!this.supported) {
      if (this.cloudEnabled()) {
        this.playCloud();

        return;
      }

      notify({ type: 'warning' }, app.translator.trans('acme-wechat-sms.forum.speak_unsupported'));

      return;
    }

    const text = firstPostText();

    if (!text) {
      notify({ type: 'warning' }, app.translator.trans('acme-wechat-sms.forum.speak_empty'));

      return;
    }

    this.unlock();

    const synth = window.speechSynthesis;
    synth.cancel();

    this.chunks = splitText(text);
    this.index = 0;
    this.stopped = false;
    this.speaking = true;
    m.redraw();

    // 给 cancel/unlock 一点时间生效，否则 iOS 上第一段会被吞掉
    setTimeout(() => {
      if (!this.stopped) {
        this.playNext();
      }
    }, 60);
  }

  /*
   * 【云端朗读】（1.8.36.20）
   *
   * 【为什么让 <audio> 直接指向接口，而不是先 fetch 拿 URL 再播】
   * iOS（含 iOS 微信）要求 play() 必须在用户手势的同一个调用栈里发起；
   * 先 fetch JSON 再在 Promise 回调里 play() 会被 Safari 直接拒绝。
   * 所以这里在点击回调里同步 new Audio(url).play()，
   * 由服务端接口完成「读缓存 / 调腾讯云合成 / 落盘」，浏览器等音频数据即可。
   * 代价是首次收听要等几秒（正文越长越久），前端用 preparing 状态给反馈。
   */
  cloudEnabled() {
    const cfg =
      (app.data && app.data.acmeWechat) ||
      (app.forum && app.forum.attribute ? app.forum.attribute('acmeWechat') : null) ||
      {};

    return !!cfg.tts;
  }

  audioUrl() {
    const api = (app.forum && app.forum.attribute('apiUrl')) || '/api';
    const id = this.discussion && this.discussion.id ? this.discussion.id() : 0;

    /*
     * 【为什么要带 v= 参数】（1.8.36.25）
     * 只带帖子 id 的话，改了后台音色 URL 完全不变，
     * 而服务端响应是 max-age=600，浏览器/微信内核会直接拿缓存的旧音频播
     * —— 表现就是「选了男声，听到的还是女声」。
     * 这里把后端下发的音色配置指纹拼上去，改配置即换 URL，缓存自动失效。
     */
    const cfg =
      (app.data && app.data.acmeWechat) ||
      (app.forum && app.forum.attribute ? app.forum.attribute('acmeWechat') : null) ||
      {};

    let url = String(api).replace(/\/$/, '') + '/acme-tts/audio?d=' + id;

    if (cfg.ttsRev) {
      url += '&v=' + cfg.ttsRev;
    }

    return url;
  }

  playCloud() {
    let audio;

    try {
      audio = new Audio();
    } catch (e) {
      audio = null;
    }

    if (!audio) {
      this.playCloudLegacy();

      return;
    }

    audio.src = this.audioUrl();
    audio.preload = 'auto';

    /*
     * 【为什么要把 audio 插进 DOM】（1.8.36.21）
     * 安卓微信 X5 内核对「凭空 new 出来、不在文档里」的 audio 常常不允许播放
     * （不报错、就是没声音）。插进去再用 CSS 挪出可视区，兼容性最好。
     */
    try {
      audio.style.position = 'fixed';
      audio.style.left = '-9999px';
      audio.style.width = '0';
      audio.style.height = '0';
      (document.body || document.documentElement).appendChild(audio);
    } catch (e) {
      // 插不进去也不影响后续，忽略
    }

    this.cloudAudio = audio;

    this.preparing = true;
    this.speaking = true;
    this.stopped = false;
    m.redraw();

    audio.onplaying = () => {
      this.preparing = false;
      m.redraw();
    };

    audio.onended = () => {
      this.finish();
    };

    audio.onerror = () => {
      this.finish();
      this.reportCloudError(0, '');
    };

    const p = audio.play();

    if (p && typeof p.catch === 'function') {
      // iOS 可能在非手势场景拒绝；真被拒时也要把原因说出来，不能一直转圈
      p.catch((err) => {
        const msg = err && err.message ? err.message : '浏览器拒绝了播放请求（通常是没有用户手势）';

        this.finish();
        this.reportCloudError(0, msg);
      });
    }
  }

  /*
   * 【失败时回读服务器真实原因】（1.8.36.21）
   *
   * <audio> 加载失败时只能拿到一个空空的 error 事件，看不到服务端返回了什么
   * —— 于是 1.8.36.20 只能笼统提示「请确认后台已开启并填好密钥」，
   * 而真实原因可能是 401 未登录 / 403 没权限 / 500 缓存目录不可写 / 502 腾讯云报错。
   * 这里用 fetch 重新请求同一个地址（带 cookie），把后端 JSON 里的 detail 读出来显示，
   * 一次点击就能定位，不用再猜。
   */
  reportCloudError(status, hint) {
    if (this.reported) {
      return;
    }

    this.reported = true;

    const done = (code, detail) => {
      const text = app.translator.trans('acme-wechat-sms.forum.speak_tts_error', {
        status: code ? String(code) : '--',
        detail: detail ? String(detail).slice(0, 200) : hint || '（服务器没有返回原因）',
      });

      notify({ type: 'warning' }, text);

      if (window.console && window.console.warn) {
        window.console.warn('[acme-tts] 云端朗读失败', code, detail || hint);
      }
    };

    if (typeof fetch !== 'function') {
      done(status, hint);

      return;
    }

    fetch(this.audioUrl(), { credentials: 'same-origin' })
      .then((r) => {
        const ct = r.headers && r.headers.get ? r.headers.get('Content-Type') || '' : '';

        if (r.ok) {
          done(
            0,
            ct.indexOf('audio') >= 0
              ? '服务器已经返回了音频，但浏览器播不出来（微信内核兼容问题）'
              : '服务器返回的不是音频：' + ct
          );

          return null;
        }

        return r.text().then((t) => {
          let detail = t;

          try {
            const j = JSON.parse(t);

            detail =
              (j.errors && j.errors[0] && j.errors[0].detail) ||
              (j.error && (j.error.detail || j.error)) ||
              t;
          } catch (e) {
            // 不是 JSON 就用原文
          }

          done(r.status, detail);
        });
      })
      .catch(() => done(0, '网络请求失败（连不上服务器或超时）'));
  }

  /*
   * 【预热】（1.8.36.21）
   * 首次收听要调腾讯云逐段合成，长帖可能要二三十秒，
   * 用户点下去干等很容易以为坏了（甚至被网关判定超时）。
   * 这里在页面打开后悄悄请求一次「只要 1 个字节」的音频，
   * 服务端照常合成并落盘，用户真点的时候直接命中缓存，秒开。
   * 只在「浏览器没有内置语音（微信 X5）且后台开了云端朗读」时才做，
   * 有内置语音的设备不会浪费任何额度。
   */
  prewarm() {
    if (this.supported || !this.cloudEnabled() || typeof fetch !== 'function') {
      return;
    }

    const id = this.discussion && this.discussion.id ? this.discussion.id() : 0;

    if (!id || PREWARMED[id]) {
      return;
    }

    PREWARMED[id] = 1;

    setTimeout(() => {
      try {
        fetch(this.audioUrl(), {
          credentials: 'same-origin',
          headers: { Range: 'bytes=0-0' },
        }).catch(() => {});
      } catch (e) {
        // 预热失败无所谓，用户点击时还会再试一次
      }
    }, 1200);
  }

  // 极老内核没有 Audio 构造器时的兜底：直接跳到音频地址（由浏览器接管播放）
  playCloudLegacy() {
    this.speaking = true;
    this.stopped = false;
    m.redraw();

    setTimeout(() => {
      this.finish();
    }, 1000);

    window.location.href = this.audioUrl();
  }

  oncreate(vnode) {
    super.oncreate(vnode);

    this.prewarm();
  }

  onremove(vnode) {
    // 离开页面必须停掉，否则会一直读到别的页面去
    this.stop();

    super.onremove(vnode);
  }

  view() {
    /*
     * 【为什么不因为「浏览器不支持」就不渲染按钮】（1.8.36.19）
     *
     * 1.8.36.15~.18 这里写的是 `if (!this.supported) return null;`
     * —— 结果手机微信里按钮**整个不见**（电脑版微信能看到）。
     * 原因是手机微信（尤其 Android 的 X5 内核）里 window.speechSynthesis 常常根本不存在，
     * 而电脑版微信是 Chromium 内核，API 齐全，所以只有手机端「按钮消失」。
     *
     * 用户看到的是「功能不存在」，比「点了没声音」更糟：没法判断是没部署还是不兼容。
     * 所以改成**永远渲染**，不支持时点击给一句明确提示（见 toggle()）。
     */
    let label = app.translator.trans('acme-wechat-sms.forum.speak_start');
    let icon = 'fas fa-volume-up';

    if (this.preparing) {
      label = app.translator.trans('acme-wechat-sms.forum.speak_preparing');
      icon = 'fas fa-spinner fa-spin';
    } else if (this.speaking) {
      label = app.translator.trans('acme-wechat-sms.forum.speak_stop');
      icon = 'fas fa-stop';
    }

    return m(
      Button,
      {
        className: 'Button AcmeSpeak' + (this.speaking ? ' AcmeSpeak--on' : '') + (this.preparing ? ' AcmeSpeak--busy' : ''),
        icon: icon,
        onclick: () => this.toggle(),
      },
      label
    );
  }
}
